import {
  AES_IV_LENGTH_BYTES,
  AES_KEY_LENGTH_BITS,
  AES_KEY_LENGTH_BYTES,
  BASE64_CHUNK_SIZE,
  BASE64_OUTPUT_BLOCK_LENGTH,
  MAX_PATH_LENGTH,
  NATIVE_ALG_CONFIG,
  NATIVE_PLATFORM_KEYS,
} from './native.constants';
import type { NativePlatform, NativeTokenPayload } from './native.types';

const toUint8Array = (data: ArrayBuffer | ArrayBufferView) =>
  data instanceof ArrayBuffer
    ? new Uint8Array(data)
    : new Uint8Array(data.buffer, data.byteOffset, data.byteLength);

const cleanBase64 = (str: string) => {
  const clean = str
    .replace(/-----BEGIN PUBLIC KEY-----|-----END PUBLIC KEY-----/g, '')
    .replace(/\s+/g, '')
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const padding =
    (BASE64_OUTPUT_BLOCK_LENGTH - (clean.length % BASE64_OUTPUT_BLOCK_LENGTH)) %
    BASE64_OUTPUT_BLOCK_LENGTH;
  return padding ? clean + '='.repeat(padding) : clean;
};

const base64ToArrayBuffer = (base64: string): ArrayBuffer => {
  const binary = atob(cleanBase64(base64));
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
};

const arrayBufferToBase64 = (buffer: ArrayBuffer | ArrayBufferView): string => {
  const bytes = toUint8Array(buffer);
  let binary = '';
  // Process in chunks to avoid call stack limits
  for (let i = 0; i < bytes.length; i += BASE64_CHUNK_SIZE) {
    binary += String.fromCharCode(...bytes.subarray(i, i + BASE64_CHUNK_SIZE));
  }
  return btoa(binary);
};

// Crypto operations
const importRsaKey = async (spki: string, hash: 'SHA-1' | 'SHA-256') =>
  crypto.subtle.importKey(
    'spki',
    base64ToArrayBuffer(spki),
    { name: 'RSA-OAEP', hash },
    false,
    ['encrypt']
  );

const generateAesKey = () =>
  crypto.subtle.generateKey(
    { name: 'AES-GCM', length: AES_KEY_LENGTH_BITS },
    true,
    ['encrypt']
  );

const wrapAesKey = async (rsaKey: CryptoKey, aesKey: CryptoKey) => {
  const rawKey = await crypto.subtle.exportKey('raw', aesKey);
  if (rawKey.byteLength !== AES_KEY_LENGTH_BYTES) {
    throw new Error(
      `Expected ${AES_KEY_LENGTH_BYTES}-byte AES key, got ${rawKey.byteLength}`
    );
  }
  return crypto.subtle.encrypt({ name: 'RSA-OAEP' }, rsaKey, rawKey);
};

export async function encryptForNativePlatform(
  plaintext: string,
  platform: NativePlatform
): Promise<NativeTokenPayload> {
  const config = NATIVE_ALG_CONFIG;
  const { kid, spkiPem } = NATIVE_PLATFORM_KEYS[platform];

  const [rsaKey, aesKey, iv] = await Promise.all([
    importRsaKey(spkiPem, config.hash),
    generateAesKey(),
    Promise.resolve(
      crypto.getRandomValues(new Uint8Array(AES_IV_LENGTH_BYTES))
    ),
  ]);

  const [ciphertext, wrappedKey] = await Promise.all([
    crypto.subtle.encrypt(
      { name: 'AES-GCM', iv },
      aesKey,
      new TextEncoder().encode(plaintext)
    ),
    wrapAesKey(rsaKey, aesKey),
  ]);

  return {
    alg: config.alg,
    kid,
    iv: arrayBufferToBase64(iv),
    ek: arrayBufferToBase64(wrappedKey),
    ct: arrayBufferToBase64(ciphertext),
  };
}

const sendTokenToNative = async (accessToken: string) => {
  try {
    const androidBridge = window.AndroidBridge;
    const iosBridge = window.webkit?.messageHandlers?.iosBridge;

    let platform: NativePlatform | null = null;
    if (androidBridge?.onAccessTokenReceived) platform = 'android';
    if (iosBridge) platform = 'ios';

    if (!platform) {
      console.warn('No native bridge available');
      return;
    }

    // TODO - SDPCDXP-534 - Passing access token for iOS directly.
    // Follow-up work will be determined after security review.
    if (platform === 'ios') {
      iosBridge?.postMessage?.(accessToken);
      return;
    }

    const payload = await encryptForNativePlatform(accessToken, platform);
    const message = JSON.stringify(payload);

    if (platform === 'android') {
      androidBridge?.onAccessTokenReceived?.(message);
      return;
    }
  } catch (error) {
    console.error('Failed to send encrypted token', error);
  }
};

const sanitizePath = (path: string): string | null => {
  if (!path || path.length > MAX_PATH_LENGTH) {
    return null;
  }

  if (typeof path !== 'string') {
    console.warn('Non-string path provided', typeof path, path);
    return null;
  }

  const clean = path.replace(/[^\w\-._~:/?#[\]@!$&'()*+,;=%]/g, '');
  if (clean.includes('..')) {
    return null;
  }

  if (clean.includes('//') && !clean.match(/^https?:\/\//)) {
    return null;
  }

  return clean.startsWith('/') || clean.match(/^https?:\/\//)
    ? clean
    : `/${clean}`;
};

export { sanitizePath, sendTokenToNative };
